import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import { Card, CardContent } from "@bcc/components/ui/card";
import { useRoasterProfile } from "@bcc/contexts/roaster-profile-context";
import { ArrowUpDown, ChevronDown, ChevronRight, Edit, FileEdit } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChangeAvailabilityPopover } from "./change-availability-popover";
import { ConfigureProductModal } from "./configure-product-modal";
import { EditProductModal } from "./edit-product-modal";
import { ProductImageLightbox } from "./product-image-lightbox";
import { ProductThumbnail } from "./product-thumbnail";
import { setProductsTableTestId } from "./products-table.utils";
import { WeeklyLimitPopover } from "./weekly-limit-popover";

export interface Variant {
  id: string;
  size: string;
  sku: string;
  status: "published" | "out_of_stock" | "draft" | "rejected";
  subscribers: number;
  purchasePrice: number;
  salePrice: number;
  commission: number;
  payout: number;
  margin: number;
  weeklyLimit: number;
  termType: "long_term" | "short_term";
  termEndDate?: string;
  expectedOutOfStockDate?: string;
  grindMappings: string[];
  stock: number;
}

export interface Product {
  id: number;
  name: string;
  sku: string;
  category: string;
  imageUrl: string;
  description: string;
  source: "manual" | "shopify";
  variants: Variant[];
}

interface ProductsTableProps {
  data: Product[];
  testId?: string;
}

// Helper Functions
const getStatusBadge = (status: Variant["status"]) => {
  const styles = {
    published:
      "bg-green-500/10 text-green-700 dark:text-green-400 hover:bg-green-500/20",
    out_of_stock:
      "bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20",
    draft:
      "bg-blue-500/10 text-blue-700 dark:text-blue-400 hover:bg-blue-500/20",
    rejected:
      "bg-red-500/10 text-red-700 dark:text-red-400 hover:bg-red-500/20",
  };

  const labels = {
    published: "Published",
    out_of_stock: "Out of Stock",
    draft: "Draft",
    rejected: "Rejected",
  };

  return { className: styles[status], label: labels[status] };
};

const getSourceBadge = (source: "manual" | "shopify") => {
  if (source === "shopify") {
    return {
      className:
        "bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30",
      label: "Shopify",
    };
  }
  return {
    className:
      "bg-gray-500/10 text-gray-700 dark:text-gray-400 border-gray-500/30",
    label: "Manual",
  };
};

// Cell Components
function ImageCell({
  isFirstVariant,
  hasMultipleVariants,
  isExpanded,
  product,
  onToggleExpansion,
  onImageClick,
}: {
  isFirstVariant: boolean;
  hasMultipleVariants: boolean;
  isExpanded: boolean;
  product: Product;
  onToggleExpansion: () => void;
  onImageClick: () => void;
}) {
  return (
    <td className="px-4 py-4">
      {isFirstVariant ? (
        <div className="flex items-center gap-2">
          {hasMultipleVariants && (
            <button
              onClick={onToggleExpansion}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label={isExpanded ? "Collapse variants" : "Expand variants"}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          )}
          <ProductThumbnail
            imageUrl={product.imageUrl}
            productName={product.name}
            onClick={onImageClick}
            allowUpload={false}
          />
        </div>
      ) : (
        <div className="w-full" />
      )}
    </td>
  );
}

function NameCell({
  isFirstVariant,
  product,
}: {
  isFirstVariant: boolean;
  product: Product;
}) {
  return (
    <td className="px-6 py-4">
      {isFirstVariant ? (
        <div className="flex flex-col">
          <span className="font-medium text-sm">{product.name}</span>
          <span className="text-xs text-muted-foreground">{product.sku}</span>
        </div>
      ) : (
        <div className="text-xs text-muted-foreground pl-4">└─</div>
      )}
    </td>
  );
}

function BagSizeCell({ variant }: { variant: Variant }) {
  return (
    <td className="px-6 py-4">
      <div className="flex flex-col">
        <span className="font-medium text-sm">{variant.size}</span>
        <span className="text-xs text-muted-foreground">{variant.sku}</span>
      </div>
    </td>
  );
}

function SourceCell({
  isFirstVariant,
  source,
}: {
  isFirstVariant: boolean;
  source: "manual" | "shopify";
}) {
  const sourceBadge = getSourceBadge(source);
  return (
    <td className="px-6 py-4">
      {isFirstVariant && (
        <Badge variant="outline" className={sourceBadge.className}>
          {sourceBadge.label}
        </Badge>
      )}
    </td>
  );
}

function StatusCell({
  variant,
  product,
  onConfigureDraft,
}: {
  variant: Variant;
  product: Product;
  onConfigureDraft: () => void;
}) {
  const statusBadge = getStatusBadge(variant.status);

  return (
    <td className="px-6 py-4">
      {variant.status === "draft" ? (
        <Badge
          variant="secondary"
          className={`${statusBadge.className} cursor-pointer flex items-center gap-1 w-fit`}
          onClick={onConfigureDraft}
        >
          <FileEdit className="h-3 w-3" />
          {statusBadge.label}
        </Badge>
      ) : (
        <ChangeAvailabilityPopover variant={variant}>
          <Badge
            variant="secondary"
            className={`${statusBadge.className} cursor-pointer`}
          >
            {statusBadge.label}
          </Badge>
        </ChangeAvailabilityPopover>
      )}
    </td>
  );
}

function VariantsCell({
  variant,
  showColumn,
}: {
  variant: Variant;
  showColumn: boolean;
}) {
  if (!showColumn) return null;

  return (
    <td className="px-6 py-4">
      <span className="text-sm text-muted-foreground">
        {variant.grindMappings.length > 0
          ? `${variant.grindMappings.length} grinds`
          : "-"}
      </span>
    </td>
  );
}

function SubscribersCell({ subscribers }: { subscribers: number }) {
  return (
    <td className="px-6 py-4">
      <span className="text-sm font-medium">{subscribers}</span>
    </td>
  );
}

function WholesalePriceCell({ price }: { price: number }) {
  return (
    <td className="px-6 py-4">
      <span className="text-sm">${price.toFixed(2)} USD</span>
    </td>
  );
}

function MsrpRrpCell({ price }: { price: number }) {
  return (
    <td className="px-6 py-4">
      <span className="text-sm font-medium">${price.toFixed(2)} USD</span>
    </td>
  );
}

function WeeklyLimitCell({ variant }: { variant: Variant }) {
  return (
    <td className="px-6 py-4">
      <WeeklyLimitPopover variant={variant}>
        <button className="text-sm text-primary hover:underline cursor-pointer">
          {variant.weeklyLimit}
        </button>
      </WeeklyLimitPopover>
    </td>
  );
}

function TermCell({ variant }: { variant: Variant }) {
  return (
    <td className="px-6 py-4">
      <Badge
        variant="outline"
        className={
          variant.termType === "long_term"
            ? "border-primary/30 text-primary"
            : "border-amber-500/30 text-amber-600 dark:text-amber-400"
        }
      >
        {variant.termType === "long_term" ? "Long-Term" : "Short-Term"}
      </Badge>
    </td>
  );
}

function ExpectedOutOfStockDateCell({ variant }: { variant: Variant }) {
  return (
    <td className="px-6 py-4">
      {variant.termType === "short_term" && variant.termEndDate ? (
        <span className="text-sm text-muted-foreground">
          {new Date(variant.termEndDate).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </span>
      ) : (
        <span className="text-sm text-muted-foreground">-</span>
      )}
    </td>
  );
}

function ActionsCell({
  isFirstVariant,
  product,
  disabled,
  onEdit,
}: {
  isFirstVariant: boolean;
  product: Product;
  disabled: boolean;
  onEdit: () => void;
}) {
  return (
    <td className="px-6 py-4 text-right">
      {isFirstVariant && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onEdit}
          className="text-primary hover:text-primary/80"
          disabled={disabled}
        >
          <Edit className="mr-2 h-4 w-4" />
          Edit
        </Button>
      )}
    </td>
  );
}

// Variant Row Component
interface VariantRowProps {
  product: Product;
  variant: Variant;
  isFirstVariant: boolean;
  hasMultipleVariants: boolean;
  isExpanded: boolean;
  showVariantsColumn: boolean;
  disableEdit: boolean;
  onToggleExpansion: () => void;
  onImageClick: () => void;
  onEdit: () => void;
  onConfigureDraft: () => void;
}

function VariantRow({
  product,
  variant,
  isFirstVariant,
  hasMultipleVariants,
  isExpanded,
  showVariantsColumn,
  disableEdit,
  onToggleExpansion,
  onImageClick,
  onEdit,
  onConfigureDraft,
}: VariantRowProps) {
  return (
    <tr className="hover:bg-muted/30 transition-colors">
      <ImageCell
        isFirstVariant={isFirstVariant}
        hasMultipleVariants={hasMultipleVariants}
        isExpanded={isExpanded}
        product={product}
        onToggleExpansion={onToggleExpansion}
        onImageClick={onImageClick}
      />
      <NameCell isFirstVariant={isFirstVariant} product={product} />
      <BagSizeCell variant={variant} />
      <SourceCell isFirstVariant={isFirstVariant} source={product.source} />
      <StatusCell
        variant={variant}
        product={product}
        onConfigureDraft={onConfigureDraft}
      />
      <VariantsCell variant={variant} showColumn={showVariantsColumn} />
      <SubscribersCell subscribers={variant.subscribers} />
      <WholesalePriceCell price={variant.purchasePrice} />
      <MsrpRrpCell price={variant.salePrice} />
      <WeeklyLimitCell variant={variant} />
      <TermCell variant={variant} />
      <ExpectedOutOfStockDateCell variant={variant} />
      <ActionsCell
        isFirstVariant={isFirstVariant}
        product={product}
        disabled={disableEdit}
        onEdit={onEdit}
      />
    </tr>
  );
}

// Main Table Component
export function ProductsTable({ data, testId }: ProductsTableProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [expandedProducts, setExpandedProducts] = useState<Set<number>>(
    new Set(),
  );
  const [configureModalOpen, setConfigureModalOpen] = useState(false);
  const [configuringProduct, setConfiguringProduct] = useState<Product | null>(
    null,
  );
  const [nameSortDir, setNameSortDir] = useState<"asc" | "desc" | null>(null);
  const { profileType, isLoaded } = useRoasterProfile();
  const { t } = useTranslation();

  const handleImageClick = (product: Product) => {
    setSelectedProduct(product);
    setLightboxOpen(true);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setEditModalOpen(true);
  };

  const handleConfigureDraft = (product: Product) => {
    setConfiguringProduct(product);
    setConfigureModalOpen(true);
  };

  const toggleProductExpansion = (productId: number) => {
    setExpandedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const handleNameSort = () => {
    setNameSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const filteredProducts = (() => {
    const base =
      profileType === "SS" ? data.filter((p) => p.source === "manual") : data;
    if (!nameSortDir) return base;
    return [...base].sort((a, b) => {
      const cmp = a.name.localeCompare(b.name);
      return nameSortDir === "asc" ? cmp : -cmp;
    });
  })();

  const showVariantsColumn = profileType !== "SS";
  const disableEdit = profileType === "SS";

  // Prevent hydration mismatch by showing loading state until client-side data is loaded
  if (!isLoaded) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          {t("views.productsTable.loading")}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid={testId ?? setProductsTableTestId("container")}>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-border bg-muted/30">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground w-[60px]">
                    {t("views.productsTable.columns.image")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground">
                    <button
                      onClick={handleNameSort}
                      className="flex items-center gap-1 hover:text-foreground transition-colors"
                    >
                      {t("views.productsTable.columns.name")}
                      <ArrowUpDown className="h-3 w-3" />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground min-w-[120px]">
                    {t("views.productsTable.columns.bagSize")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground">
                    {t("views.productsTable.columns.source")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground">
                    {t("views.productsTable.columns.status")}
                  </th>
                  {showVariantsColumn && (
                    <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground">
                      {t("views.productsTable.columns.variants")}
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground">
                    {t("views.productsTable.columns.subscribers")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground">
                    {t("views.productsTable.columns.wholesalePrice")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground">
                    {t("views.productsTable.columns.msrpRrp")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground">
                    {t("views.productsTable.columns.weeklyLimit")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground">
                    {t("views.productsTable.columns.term")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground">
                    {t("views.productsTable.columns.expectedOutOfStock")}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-muted-foreground">
                    {t("views.productsTable.columns.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredProducts.map((product) => {
                  const isExpanded = expandedProducts.has(product.id);
                  const hasMultipleVariants = product.variants.length > 1;
                  const variantsToShow = isExpanded
                    ? product.variants
                    : [product.variants[0]!];

                  return variantsToShow.map((variant, variantIndex) => {
                    const isFirstVariant = variantIndex === 0;

                    return (
                      <VariantRow
                        key={variant.id}
                        product={product}
                        variant={variant}
                        isFirstVariant={isFirstVariant}
                        hasMultipleVariants={hasMultipleVariants}
                        isExpanded={isExpanded}
                        showVariantsColumn={showVariantsColumn}
                        disableEdit={disableEdit}
                        onToggleExpansion={() =>
                          toggleProductExpansion(product.id)
                        }
                        onImageClick={() => handleImageClick(product)}
                        onEdit={() => handleEdit(product)}
                        onConfigureDraft={() => handleConfigureDraft(product)}
                      />
                    );
                  });
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {selectedProduct && (
        <ProductImageLightbox
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          imageUrl={selectedProduct.imageUrl}
          productName={selectedProduct.name}
        />
      )}

      {editingProduct && (
        <EditProductModal
          product={editingProduct}
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
          onSave={() => {
            setEditModalOpen(false);
            // Refresh data here
          }}
        />
      )}

      {configuringProduct && (
        <ConfigureProductModal
          product={configuringProduct}
          open={configureModalOpen}
          onOpenChange={setConfigureModalOpen}
          onSave={() => {
            setConfigureModalOpen(false);
            // Refresh data here
          }}
        />
      )}
    </>
  );
}
